import { Component, Input } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { List as ImmutableList } from 'immutable';
import { debounce } from 'lodash-decorators';
import { List } from '../../constants-types/PiList.model';
import { Group } from '../../core-data-model/Group.model';
import { GroupsDataService } from '../../core-services/Groups.dataservice';
import { DateFormatUtility } from '../../core-utilities/DateFormat.utility';
import { Util } from '../../core-utilities/Util';
import { TranslationService } from '../../translations';

@Component({
    selector: 'group-typeahead',
    template: `
        <array-to-single-model [(ngModel)]="group">
            <ok-pillbox-with-typeahead
                #childComponent
                [typeaheadList]="groupPossibilities"
                (typeaheadOnTextChange)="typeaheadTextChange($event)"
                [typeaheadPlaceholder]="TR.key('editTeams.selectTeam')"
                [pillboxLimit]="1"
                [searchStyling]="true"
                [isDisabled]="isDisabled"
                [isLoadingPossibilities]="filteringGroups"
            >
                <ng-template #pillbox let-item="item">
                    {{ displayFunction(item) }}
                </ng-template>
                <ng-template #typeahead let-item="item">
                    {{ displayFunction(item) }}
                </ng-template>
            </ok-pillbox-with-typeahead>
        </array-to-single-model>
    `,
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: GroupTypeaheadComponent,
            multi: true
        }
    ],
    standalone: false
})
export class GroupTypeaheadComponent implements ControlValueAccessor {
    // This is what will be bound to externally
    _group: Group;
    // Value needed by ControlValueAccessor - it is a no-op that is replaced by framework
    propagateChange = (_: any) => {
        /* No change propagated */
    };
    groupPossibilities: List<Group> = ImmutableList();
    textInBox: string;
    filteringGroups = false;
    @Input()
    isDisabled: boolean;
    @Input()
    isTeamDiscovery = false;
    @Input()
    teamDiscoverySessions: string[] = [];

    constructor(
        public TR: TranslationService,
        public util: Util,
        public date: DateFormatUtility,
        private groupsDataService: GroupsDataService
    ) {}

    typeaheadTextChange(textInBox: string) {
        this.filteringGroups = true;
        this.filterGroups(textInBox);
    }

    @debounce(250)
    private filterGroups(textInBox: string): void {
        this.groupPossibilities = ImmutableList(
            this.groupsDataService
                .getCompleteGroup()
                .filter((group) => group.isEditableByUser)
        );
        if (textInBox && textInBox.trim() !== '') {
            this.groupPossibilities = this.groupPossibilities.filter((group) =>
                group.name
                    .toLocaleLowerCase()
                    .includes(textInBox.toLocaleLowerCase())
            );
        }
        this.filteringGroups = false;
    }

    displayFunction(item: Group) {
        if (item instanceof Group) {
            return this.isTeamDiscovery
                ? this.getTeamMemberText(item)
                : item.name;
        }
        return '';
    }

    getTeamMemberText(item: Group) {
        return this.util.strings.formatString(
            item.peopleAndJobCount === 1
                ? this.TR.key('teams.team-member-count')
                : this.TR.key('teams.team-members-count'),
            item.name,
            item.peopleAndJobCount
        );
    }

    /* Meet ControlValueAccessor Interface */
    get group(): Group {
        return this._group;
    }
    set group(val: Group) {
        this._group = val || null;
        this.propagateChange(this._group);
    }
    // Do NOT propagate changes up here
    writeValue(value: Group): void {
        this._group = value;
    }
    registerOnChange(fn: any): void {
        this.propagateChange = (changed) => fn.call(this, changed);
    }
    registerOnTouched(): void {
        /* No callback function registered OnTouched */
    }
    /** ***************************************/
}
